"use client";

import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import { SpecialRow } from "@/data/specials";
import { SupabaseClient } from "@supabase/supabase-js";
import { getComedianStripeProducts } from "@/data/subscriptions";
import { logException } from "@/analytics/sentry";

export const checkUserAccess = async (
  session: any,
  comedian: Comedian,
  special: SpecialRow
): Promise<boolean> => {
  if (!session?.user) {
    return false;
  }

  try {
    const params = new URLSearchParams({
      comedianId: comedian.id,
      specialId: special.id,
    });

    const response = await fetch(`/api/content/check-access?${params}`);
    if (!response.ok) {
      throw new Error("Failed to check access");
    }

    const { hasAccess } = await response.json();
    return hasAccess;
  } catch (error) {
    logException(error);
    return false;
  }
};

export const sendSubscriptionConfirmationEmail = async (
  userId: string,
  email: string,
  amount: number
): Promise<void> => {
  await fetch("/api/billing-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      totalPrice: (amount / 100).toFixed(2),
      email: email,
      user_id: userId,
    }),
  });
};

export const clearPaymentStorage = (
  setLocalPaymentIntentId?: (id: null) => void,
  preservePaymentCompleted: boolean = false
): void => {
  if (typeof window !== "undefined") {
    const preventClear = window.localStorage.getItem("prevent_storage_clear");
    if (preventClear === "true") {
      return;
    }

    window.localStorage.removeItem("paymentClientSecret");
    window.localStorage.removeItem("paymentMethodId");
    window.localStorage.removeItem("paymentIntentId");
    window.localStorage.removeItem("paymentEmail");
    window.localStorage.removeItem("subscriptionId");

    if (
      window.location.href.includes("/checkout/") &&
      !preservePaymentCompleted
    ) {
      window.localStorage.removeItem("payment_completed");
    }

    const url = new URL(window.location.href);
    if (
      url.searchParams.has("payment_intent_id") ||
      url.searchParams.has("followComedian")
    ) {
      url.searchParams.delete("payment_intent_id");
      url.searchParams.delete("followComedian");
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  if (setLocalPaymentIntentId) {
    setLocalPaymentIntentId(null);
  }
};

export const getComedianProduct = async (
  supabase: SupabaseClient<Database>,
  comedianId: string
) => {
  return await getComedianStripeProducts(supabase, comedianId);
};
